import { type ReactNode } from 'react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Activity,
  Cable,
  Cloud,
  ExternalLink,
  KeyRound,
  Radio,
  Router,
  Server,
  ServerCog,
  Waypoints,
} from 'lucide-react';
import type { Olt, Page, SystemInfo, Tr069ProfileHealth } from '@simpleacs/shared';
import { DEFAULT_SETTINGS_TAB, resolveSettingsTab, type SettingsTab } from '@simpleacs/shared';
import { api } from '../lib/api';
import { formatRelative } from '../lib/format';
import { useSession } from '../lib/session';
import { TimingsCard } from './settings/TimingsCard';
import { SmartOltImportTab } from './SmartOltImport.js';
import { StorageCard } from './settings/StorageCard';
import { MapProviderCard } from './settings/MapProviderCard';
import { EmailTab } from './settings/EmailTab';
import { WhiteLabelTab } from './settings/WhiteLabelTab';
import { StatusPageTab } from './settings/StatusPageTab';
import { NtpTab } from './settings/NtpTab';
import { AcsAuthTab } from './settings/AcsAuthTab';
import { SmsTab } from './settings/SmsTab';
import {
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Pill,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

/**
 * Settings → Main, slimmed by the IA rework to what settings ARE: values you
 * change. The status tiles, Services card and platform cards (GenieACS /
 * Deployment / Tunnels) migrated to Infrastructure → Platform, where each row
 * opens the page that manages it. Configuration stays read-only — a CWMP typo
 * takes the fleet offline, so edits are a deploy-time decision.
 */
const TABS: Array<{ id: SettingsTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  // The time servers the whole fleet starts from. A value you change, which is
  // what this page is for — and keeping it once is the entire point: an OLT
  // onboarded with the box left empty is the one whose clock nobody notices.
  { id: 'ntp', label: 'NTP servers' },
  /*
   * Storage and the map provider each get a tab because each has a ROW on
   * Infrastructure → Platform, and a row that says "local directory · on this
   * server only" has to be able to open the card that says why. As cards on
   * Overview they had no address of their own, so both rows could only link at
   * the page as a whole and leave the operator to find them.
   */
  { id: 'storage', label: 'File storage' },
  { id: 'map', label: 'Map provider' },
  // Where mail goes out from, and the record of what went. A tab rather than a
  // card because the second half of it — "did that invoice go out" — is a
  // question somebody arrives at this page already asking.
  // 'Email (SMTP)', not 'Email': the operator went looking for an SMTP tab
  // and did not find this one. The relay is the thing being configured here,
  // so the tab should answer to the word somebody arrives with.
  { id: 'email', label: 'Email (SMTP)' },
  { id: 'white-label', label: 'White labeling' },
  // A place you GO to, not a card to hunt for: its whole job is handing the
  // superadmin the embed code (operator's call).
  { id: 'status-page', label: 'Status page' },
  // Setting up an import is configuration, so it lives with the other values
  // rather than holding a permanent place in the nav (operator's call).
  { id: 'smartolt', label: 'SmartOLT import' },
  // Beside the other third-party connections, for the same reason SmartOLT is
  // here: it is a credential plus a request shape, and that is configuration.
  { id: 'sms', label: 'SMS gateway' },
  // Last, and deliberately not tucked into Overview: the way BACK from a mode
  // that locked the fleet out has to be an address you can reach directly.
  { id: 'acs-auth', label: 'ACS authentication' },
];

/**
 * The settings area, in tabs. The Billing tab that sat here as a placeholder is
 * gone — the real /billing page replaced it whole.
 *
 * **The tab lives in the URL** (`/settings?tab=storage`), not in `useState`.
 * A tab nobody can link to is a tab that can only be reached by describing it
 * in words — which is exactly why the Platform index's Storage and Map
 * provider rows pointed at the page as a whole and left the operator hunting.
 *
 * A query parameter rather than a path segment on purpose. `/settings` already
 * has real sub-PAGES under it — `/settings/tr069-profiles`, `/settings/api-keys`,
 * `/settings/presets`, `/settings/speed-profiles` — and a `/settings/:tab`
 * route would make `/settings/storage` and `/settings/api-keys` look like the
 * same kind of address when one is a tab on this page and the other is a page
 * of its own.
 *
 * Switching tabs REPLACES the history entry rather than pushing one. Tabs are
 * not navigation steps: an operator who arrives from Infrastructure → Platform,
 * glances at three tabs and presses Back means "take me back to Platform", not
 * "walk me back through the tabs I just looked at". The address bar still shows
 * the current tab, so the link is still copyable and bookmarkable — which was
 * the whole point.
 */
export function SettingsPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  // The endpoint is public either way; the tab is for whoever runs the
  // platform, like the other infrastructure surfaces.
  const tabs = TABS.filter((t) => {
    if (t.id === 'status-page') return can('infra.manage');
    // Reaches into the GenieACS deployment itself, not an org's configuration.
    if (t.id === 'acs-auth') return can('infra.manage');
    // Same gate the tab's own controls use, so it is never an empty panel.
    if (t.id === 'smartolt') return can('olt.manage');
    // Platform-wide, and it holds a third-party credential — the same gate the
    // storage and map cards carry. The routes behind it are superadmin-only, so
    // showing the tab to anybody else would only offer them a 403.
    if (t.id === 'sms') return can('infra.manage');
    // Readable by anyone who can see the fleet; the form itself is read-only
    // without settings.manage, which is how the other cards here behave.
    if (t.id === 'ntp') return can('olt.read');
    // The gate these two carried as cards on Overview, kept exactly: nothing
    // becomes visible to anybody who could not already see it.
    // The relay is platform plumbing and its password is a platform secret:
    // the same gate storage and the map provider carry.
    if (t.id === 'storage' || t.id === 'map' || t.id === 'email') return can('infra.manage');
    return true;
  });

  /**
   * Resolved against the tabs THIS viewer has, so three things land on Overview
   * instead of on a blank page: a bare `/settings`, a tab id that does not
   * exist, and a real tab that this viewer's role hides.
   */
  const tab = resolveSettingsTab(
    params.get('tab'),
    tabs.map((t) => t.id),
  );

  const select = (next: SettingsTab) => {
    const nextParams = new URLSearchParams(params);
    // The default tab is a bare `/settings` — the address already in the nav,
    // in redirects and in bookmarks. Spelling it `?tab=overview` would quietly
    // create a second URL for one page.
    if (next === DEFAULT_SETTINGS_TAB) nextParams.delete('tab');
    else nextParams.set('tab', next);
    setParams(nextParams, { replace: true });
  };

  return (
    <>
      <PageHeader title="Settings" description="The values the platform runs by, and whose product it looks like." />

      <div className="mb-5 flex gap-1 overflow-x-auto border-b border-line">
        {tabs.map((entry) => (
          <button
            key={entry.id}
            onClick={() => select(entry.id)}
            className={
              tab === entry.id
                ? 'border-b-2 border-primary px-4 py-2.5 text-sm font-medium text-primary'
                : 'border-b-2 border-transparent px-4 py-2.5 text-sm text-muted hover:text-ink'
            }
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'ntp' && <NtpTab />}
      {tab === 'storage' && <StorageCard />}
      {tab === 'map' && <MapProviderCard />}
      {tab === 'email' && <EmailTab />}
      {tab === 'white-label' && <WhiteLabelTab />}
      {tab === 'status-page' && <StatusPageTab />}
      {tab === 'smartolt' && <SmartOltImportTab />}
      {tab === 'sms' && <SmsTab />}
      {tab === 'acs-auth' && <AcsAuthTab />}
    </>
  );
}

function OverviewTab() {
  const { activeOrg, session, can } = useSession();
  const needsOrg = session?.user.role === 'superadmin' && !activeOrg;
  const canReadFleet = can('olt.read');
  const canManageInfra = can('infra.manage');

  const systemQuery = useQuery({
    queryKey: ['system-info'],
    queryFn: () => api.get<SystemInfo>('/settings/system'),
    refetchInterval: 30_000,
  });
  const healthQuery = useQuery({
    queryKey: ['tr069-profiles', 'health'],
    queryFn: () => api.get<{ items: Tr069ProfileHealth[] }>('/tr069-profiles/health'),
    enabled: canReadFleet && !needsOrg,
    refetchInterval: 30_000,
  });
  const oltsQuery = useQuery({
    queryKey: ['olts', activeOrg?.id ?? 'all'],
    queryFn: () => api.get<Page<Olt>>('/olts'),
    enabled: canReadFleet,
    refetchInterval: 60_000,
  });

  const info = systemQuery.data;
  const profiles = healthQuery.data?.items ?? [];
  const profilesReachable = profiles.filter((p) => p.reachable).length;
  const olts = oltsQuery.data?.items ?? [];
  const oltsUp = olts.filter((o) => o.status === 'up').length;
  const onusOnline = olts.reduce((n, o) => n + o.onuOnlineCount, 0);
  const onusTotal = olts.reduce((n, o) => n + o.onuCount, 0);

  const acsOk = info?.acs.ok ?? null;
  const live = info?.execAdapter === 'shell';

  return (
    <>
      <p className="mb-4 text-sm text-muted">
        {activeOrg
          ? `Live health of the platform and ${activeOrg.name}.`
          : 'Live health of the running platform.'}
      </p>

      {systemQuery.error ? (
        <Card>
          <div className="p-5">
            <ErrorNotice error={systemQuery.error} />
          </div>
        </Card>
      ) : (
        <div className="space-y-5">
          {/* The IA rework slimmed this page to what settings ARE: values you
              change. The status tiles, Services card and the platform cards
              (GenieACS / Deployment / Tunnels) migrated to Infrastructure →
              Platform, where each row opens the page that manages it. */}
          {canManageInfra && (
            <p className="rounded-lg border border-line bg-surface px-4 py-2.5 text-sm text-muted">
              Live platform state — GenieACS, tunnels, deployment — moved to{' '}
              <Link className="text-primary hover:underline" to="/infrastructure">
                Infrastructure → Platform
              </Link>
              .
            </p>
          )}
          <CardRow
            cards={[
              <ConfigurationCard key="config" info={info} loading={systemQuery.isLoading} />,
              <TimingsCard key="timings" />,
            ]}
          />
          {/* File storage and Map provider moved to tabs of their own, so the
              two rows that watch them on Infrastructure → Platform have
              somewhere to land. Neither card changed and neither gate changed;
              they are one labelled click away instead of somewhere down this
              page. */}
          <CardRow
            cards={[
              <Tr069HealthCard
                key="tr069"
                profiles={profiles}
                loading={healthQuery.isLoading}
                needsOrg={needsOrg}
                canRead={canReadFleet}
              />,
              canReadFleet ? (
                <OltFleetCard key="fleet" olts={olts} loading={oltsQuery.isLoading} />
              ) : null,
            ]}
          />
        </div>
      )}
    </>
  );
}


/**
 * One row of cards, sized to how many it actually has.
 *
 * Rows are fixed pairings rather than one flowing grid because a card an
 * operator cannot see would otherwise pull the next one up into its place and
 * scramble the arrangement for everybody without `infra.manage`.
 */
function CardRow({ cards }: { cards: Array<ReactNode | null> }) {
  const present = cards.filter(Boolean);
  if (present.length === 0) return null;
  return (
    <div
      className={clsx(
        'grid gap-5',
        present.length === 2 && 'xl:grid-cols-2',
        present.length >= 3 && 'xl:grid-cols-3',
      )}
    >
      {present}
    </div>
  );
}


/** One running subsystem: a status pill, a couple of detail lines, and where to manage it. */
function ServiceRow({
  icon,
  name,
  tone,
  status,
  detail,
  to,
  linkLabel,
  external,
}: {
  icon: ReactNode;
  name: string;
  tone: 'success' | 'warn' | 'danger' | 'neutral';
  status: string;
  detail: ReactNode;
  to?: string;
  linkLabel?: string;
  external?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 px-5 py-3.5">
      <div className="flex min-w-0 gap-3">
        <span className="mt-0.5 text-muted">{icon}</span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-ink">{name}</span>
            <Pill state tone={tone} dot>
              {status}
            </Pill>
          </div>
          <div className="mt-1 space-y-0.5 font-mono text-xs text-muted">{detail}</div>
        </div>
      </div>
      {to &&
        (external ? (
          <a
            href={to}
            target="_blank"
            rel="noreferrer"
            className="inline-flex shrink-0 items-center gap-1 text-xs text-link hover:underline"
          >
            {linkLabel}
            <ExternalLink className="size-3" />
          </a>
        ) : (
          <Link to={to} className="shrink-0 text-xs text-link hover:underline">
            {linkLabel}
          </Link>
        ))}
    </div>
  );
}

/** The platform subsystems: the ACS, both tunnels, and the device poller. */
/** The org's ACS profiles and whether each is answering — the detail behind the tile. */
function Tr069HealthCard({
  profiles,
  loading,
  needsOrg,
  canRead,
}: {
  profiles: Tr069ProfileHealth[];
  loading: boolean;
  needsOrg: boolean;
  canRead: boolean;
}) {
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Server className="size-4 text-muted" />
          TR-069 profiles
        </span>
      }
      action={
        canRead && !needsOrg ? (
          <Link to="/settings/tr069-profiles" className="text-xs text-link hover:underline">
            Manage
          </Link>
        ) : null
      }
    >
      {!canRead ? (
        <EmptyState icon={<Server className="size-8" />} title="Not available for your role" />
      ) : needsOrg ? (
        <EmptyState
          icon={<Server className="size-8" />}
          title="Select an organization"
          description="Profiles are org-wide — pick the organization in the top bar."
        />
      ) : loading ? (
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      ) : profiles.length === 0 ? (
        <EmptyState
          icon={<Server className="size-8" />}
          title="No TR-069 profiles"
          description="The local GenieACS appears here on first use."
        />
      ) : (
        <div className="divide-y divide-line">
          {profiles.map((p) => (
            <div key={p.profileId} className="flex items-center justify-between gap-3 px-5 py-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-ink">{p.name}</div>
                <div className="mt-1 flex flex-wrap items-center gap-1">
                  {p.services.map((s) => (
                    <span
                      key={s.name}
                      title={`${s.url}${s.ok && s.latencyMs != null ? ` · ${s.latencyMs} ms` : ''}`}
                    >
                      <Pill state tone={s.ok ? 'success' : 'neutral'}>{s.name}</Pill>
                    </span>
                  ))}
                </div>
              </div>
              <Pill state tone={p.reachable ? 'success' : 'danger'} dot>
                {p.reachable ? 'reachable' : 'unreachable'}
              </Pill>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/** The org's OLTs, reachability and ONU load — the fleet the platform is driving. */
function OltFleetCard({ olts, loading }: { olts: Olt[]; loading: boolean }) {
  const toneFor = (s: Olt['status']) =>
    s === 'up' ? 'success' : s === 'down' ? 'danger' : 'neutral';

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Cable className="size-4 text-muted" />
          OLT fleet
          <span className="text-xs font-normal text-faint">{olts.length}</span>
        </span>
      }
      action={
        <Link to="/olts" className="text-xs text-link hover:underline">
          View all
        </Link>
      }
    >
      {loading ? (
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      ) : olts.length === 0 ? (
        <EmptyState
          icon={<Cable className="size-8" />}
          title="No OLTs onboarded"
          description="Add a chassis to start syncing its PON inventory."
        />
      ) : (
        <Table>
          <THead>
            <th className="th">Name</th>
            <th className="th">Status</th>
            <th className="th">ONUs online</th>
            <th className="th">Last sync</th>
          </THead>
          <TBody>
            {olts.map((olt) => (
              <TRow key={olt.id}>
                <td className="td">
                  <Link to={`/olts/${olt.number ?? olt.id}`} className="font-medium text-link hover:underline">
                    {olt.name}
                  </Link>
                  <span className="ml-2 font-mono text-xs text-faint">
                    {olt.vendor}
                    {olt.model ? ` · ${olt.model}` : ''}
                  </span>
                </td>
                <td className="td">
                  <Pill state tone={toneFor(olt.status)} dot>
                    {olt.status}
                  </Pill>
                </td>
                <td className="td tabular-nums text-muted">
                  <span className="inline-flex items-center gap-1.5">
                    <Radio className="size-3.5 text-faint" />
                    {olt.onuOnlineCount.toLocaleString()} / {olt.onuCount.toLocaleString()}
                  </span>
                </td>
                <td className="td text-muted">
                  {olt.lastPolledAt ? (
                    formatRelative(olt.lastPolledAt)
                  ) : (
                    <span className="text-faint">never</span>
                  )}
                  {/*
                    Only a genuine comms failure, judged by `status === 'down'`
                    — never by `last_error` truthiness. That field carries the
                    sweep's routine notes ("4 ONU state change(s) recorded",
                    "1361 ONUs across 32 ports") as often as its failures, so
                    keying a red badge off it flagged a healthy chassis. The full
                    note still reads on the OLT's Details tab.
                  */}
                  {olt.status === 'down' && !olt.disabledAt && (
                    <span
                      className="ml-2 text-xs text-danger"
                      title={olt.lastError ?? 'No response over SNMP or CLI'}
                    >
                      unreachable
                    </span>
                  )}
                </td>
              </TRow>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}

/**
 * The effective configuration, read-only. Values come from the server's .env —
 * deliberately not editable here, because a typo in the CWMP URL takes the whole
 * fleet offline; changing them is a deploy-time decision.
 */
function ConfigurationCard({ info, loading }: { info: SystemInfo | undefined; loading: boolean }) {
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <KeyRound className="size-4 text-muted" />
          Configuration
          <span className="text-xs font-normal text-faint">read-only</span>
        </span>
      }
    >
      {loading || !info ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : (
        <>
          {/* One column: the card is half the page now, and a value like the
              CWMP URL has nowhere to go in a quarter of it. */}
          <dl className="grid">
            {(
              [
                ['CWMP URL (devices inform to)', info.cwmpUrl],
                ['GenieACS NBI', info.nbiUrl],
                ['File server', info.fileServerUrl],
                ['GenieACS UI', info.genieUiUrl || '—'],
                ['Device poll interval', `${info.pollIntervalMs / 1000}s`],
                ['Exec adapter', info.execAdapter],
                ['WireGuard', `${info.wgInterface} · ${info.wgEndpoint} · pool ${info.wgPool}`],
                ['OpenVPN', `${info.ovpnServer} · ${info.ovpnSubnet}`],
              ] as Array<[string, string]>
            ).map(([label, value]) => (
              <div
                key={label}
                className="flex items-baseline justify-between gap-4 border-b border-line px-5 py-2.5"
              >
                <dt className="text-sm text-muted">{label}</dt>
                <dd className="truncate font-mono text-xs text-ink">{value}</dd>
              </div>
            ))}
          </dl>
          <p className="px-5 py-2.5 text-[11px] text-faint">
            Values come from the server's .env and change with a deploy — a typo in the CWMP URL
            takes the fleet offline, so there is deliberately no edit form here.
            {info.acs.error ? ` ACS error: ${info.acs.error}` : ''}
          </p>
        </>
      )}
    </Card>
  );
}
